import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, Heart, GraduationCap, Briefcase, ArrowRight, MessageCircle } from 'lucide-react';

const sessions = [
  {
    id: 'portrait',
    icon: Camera,
    label: 'Family & Portraits',
    description: 'Families, seniors, couples — real moments, not stiff smiles.',
    price: 'Custom',
    link: '/contact?consultation=true',
    popular: true,
  },
  {
    id: 'brand',
    icon: Briefcase,
    label: 'Brand & Headshots',
    description: 'Content that actually looks like you, not a stock photo.',
    price: 'Custom',
    link: '/contact?consultation=true',
    popular: true,
  },
  {
    id: 'wedding',
    icon: Heart,
    label: 'Wedding & Events',
    description: 'Your love story, captured with intention and artistry.',
    price: 'Custom',
    link: '/contact?consultation=true',
    popular: false,
  },
  {
    id: 'senior',
    icon: GraduationCap,
    label: 'Senior Portraits',
    description: 'Celebrate this milestone with portraits as unique as you are.',
    price: 'Custom',
    link: '/contact?consultation=true',
    popular: false,
  },
];

export function SessionFinder() {
  const [selected, setSelected] = useState<string | null>(null);
  const selectedSession = sessions.find(s => s.id === selected);

  return (
    <section className="relative py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10">
          <span className="eyebrow-label">
            Not sure where to start?
          </span>
          <h2 className="font-display text-3xl md:text-5xl text-foreground mt-4 text-ethereal">
            Find Your Perfect Session
          </h2>
          <div className="filigree-divider max-w-xs mx-auto mt-4">
            <span className="text-primary/55 text-sm">✦</span>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 max-w-4xl mx-auto">
          {sessions.map((session) => (
            <button
              key={session.id}
              onClick={() => setSelected(selected === session.id ? null : session.id)}
              aria-pressed={selected === session.id}
              className={`ethereal-panel relative flex min-h-[168px] flex-col p-5 text-left transition-all duration-500 group md:min-h-[212px] md:p-6 ${
                selected === session.id
                  ? 'border-primary/55 bg-primary/8 ring-1 ring-primary/35'
                  : 'hover:border-primary/40'
              }`}
            >
              <div className="mb-4 min-h-[1.75rem]">
                {session.popular ? (
                  <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-3 py-1 font-cinzel text-[9px] uppercase tracking-[0.28em] text-primary">
                    Popular
                  </span>
                ) : (
                  <span aria-hidden className="block h-7" />
                )}
              </div>
              <session.icon className={`h-8 w-8 mb-5 transition-colors ${
                selected === session.id ? 'text-primary' : 'text-muted-foreground group-hover:text-primary'
              }`} strokeWidth={1.45} />
              <h3 className="font-serif text-lg md:text-xl text-foreground mb-2 leading-tight">
                {session.label}
              </h3>
              <p className="text-sm text-muted-foreground hidden md:block leading-relaxed mt-1">
                {session.description}
              </p>
              <p className="text-xs text-primary mt-auto pt-4 font-cinzel tracking-[0.2em] uppercase">{session.price}</p>
            </button>
          ))}
        </div>

        {/* Expanded selection panel */}
        {selectedSession && (
          <div className="mt-6 max-w-2xl mx-auto text-center animate-fade-in">
            <div className="ethereal-panel px-6 py-6">
              <p className="font-accent text-xl text-foreground/86 mb-5 md:hidden">{selectedSession.description}</p>
              <p className="hidden md:block font-accent text-xl text-foreground/86 mb-5">{selectedSession.description}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/booking"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-gold px-6 py-3 font-cinzel text-[11px] tracking-[0.24em] uppercase text-primary-foreground shadow-ethereal transition-all hover:shadow-gold"
              >
                Book {selectedSession.label} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to={selectedSession.link}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-primary/35 bg-card/40 px-6 py-3 font-cinzel text-[11px] tracking-[0.24em] uppercase text-foreground transition-all hover:border-primary hover:bg-primary/14"
              >
                See Pricing
              </Link>
            </div>
            </div>
          </div>
        )}

      </div>
    </section>
  );
}
